<template>
  <v-flex xs12 lg9>
    <v-card class="py-4">
      <v-flex xs12 px-4 py-2 v-if="err">
        <v-alert :value="true" type="error">{{err}}</v-alert>
      </v-flex>
      <v-flex xs12 px-4 py-2 v-if="success">
        <v-alert :value="true" type="success">{{success}}</v-alert>
      </v-flex>
      <v-form v-model="valid">
        <v-layout row wrap justify-space-between align-start class="px-2">
          <v-flex xs12 sm6 px-3 v-if="!isLocalAdmin">
            <v-autocomplete
              :items="companies"
              :label="$lang.actions.selectCompany"
              v-model="terminal.companyId"
              item-text="name"
              item-value="id"
              required
              :readonly="isEdit"
              :loading="companyLoading"
              :rules="[v => !!v || `${$lang.errors.missingCompany}`]"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              :label="$lang.labels.terminalName"
              v-model="terminal.name"
              required
              :rules="[v => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              :label="$lang.labels.terminalIdentifier"
              v-model="terminal.terminalIdentifier"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              :label="$lang.labels.ipAddress"
              v-model="terminal.ipAddress"
              required
              :rules="[v => !!v || 'Required']"
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              :label="$lang.labels.portNumber"
              v-model="terminal.port"
              :rules="[v => checkPort(v), v => !!v || 'Required']"
              required
            />
          </v-flex>
          <v-flex xs12 sm6 px-3>
            <v-text-field
              :label="$lang.labels.maxRetriesOnErrors"
              v-model="terminal.maxRetriesOnErrors"
              :rules="[v => checkMaxRetries(v), v => !!v || 'Required']"
              required
            />
          </v-flex>
          <v-flex xs6 px-3>
            <v-btn
              :disabled="!hasPublicKeyBE"
              color="primary mx-0"
              @click="isTerminal = true; updatePassword = true"
            >
              {{ $lang.labels.updatePassword }}
            </v-btn>
          </v-flex>
          <v-flex xs3 px-3>
            <h3>{{ $lang.labels.automaticPrint }}</h3>
            <v-checkbox
              v-model="terminal.automaticPrintMerchantReceipt"
              :label="$lang.labels.merchantPrint"
              color="primary"
              hide-details
            />
            <v-checkbox
              v-model="terminal.automaticPrintCustomerReceipt"
              :label="$lang.labels.customerPrint"
              color="primary"
              hide-details
            />
          </v-flex>
          <v-flex xs3 px-3>
            <h3>{{ $lang.labels.switchReceiptType }}</h3>
            <v-radio-group v-model="terminal.switchReceiptType">
              <v-radio
                color="primary"
                :label="$lang.labels.merchant"
                value="merchant"
              ></v-radio>
              <v-radio
                color="primary"
                :label="$lang.labels.customer"
                value="customer"
              ></v-radio>
            </v-radio-group>
          </v-flex>
        </v-layout>
        <v-layout row wrap justify-space-between class="mt-3 px-2">
          <v-text-field
            v-if="isEdit && createdOn"
            :label="$lang.labels.created"
            v-model="createdOn"
            readonly
            class="min-w max-w mx-3"
          />
          <v-text-field
            v-if="isEdit && modifiedOn"
            :label="$lang.labels.modified"
            v-model="modifiedOn"
            readonly
            class="min-w max-w mx-3"
          />
        </v-layout>
        <v-layout row wrap justify-space-between align-content-center class="px-2">
          <v-btn
            flat class="mx-3"
            @click="$router.push({ name: 'paymentTerminals' })">
            <v-icon>keyboard_arrow_left</v-icon>
            {{$lang.actions.close}}
          </v-btn>
          <v-btn
            class="primary mx-3"
            :disabled="!valid"
            @click="submit">
            {{ isEdit ? $lang.actions.update : $lang.actions.create}}
          </v-btn>
        </v-layout>
      </v-form>
      <v-dialog persistent v-if="updatePassword" v-model="updatePassword" max-width="500">
        <change-password
          @close="updatePassword = false; isTerminal = false;" :isTerminal="isTerminal"
        />
      </v-dialog>
    </v-card>
  </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    components: {
      changePassword: () => import('../modals/ChangePassword'),
    },
    data: () => ({
      isLocalAdmin: false,
      err: '',
      success: '',
      valid: false,
      isEdit: false,
      companyLoading: true,
      companies: [],
      terminal: {
        id: '',
        companyId: '',
        name: '',
        ipAddress: '',
        port: '',
        maxRetriesOnErrors: '3',
        status: '',
        passwordMask: '',
        automaticPrintMerchantReceipt: false,
        automaticPrintCustomerReceipt: false,
        automaticPrint: false,
        switchReceiptType: 'merchant'
      },
      createdOn: '',
      modifiedOn: '',
      hasPublicKeyBE: false,
      updatePassword: false,
    }),
    watch: {},
    computed: {},
    methods: {
      checkPort(val) {
        if (Number(val) < 0 || Number(val) > 65535) return 'Port outside valid range';
        return true;
      },
      checkMaxRetries(val) {
        if (Number(val) < 1 || Number(val) > 32) return 'Max retries outside valid range';
        return true;
      },
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
      },
      init() {
        return new Promise((resolve) => {
          const roles = JSON.parse(this.$cookie.get('roles') || '[]');
          this.isLocalAdmin = roles.includes('ROLE_SUN_LOCAL_ADMIN');
          this.$http.get('company')
            .then((res) => {
              resolve()
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                if (this.isLocalAdmin && this.companies.length === 1) this.terminal.companyId = this.companies[0].id;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            });
        });
      },
      submit() {
        let url = this.isEdit ? `paymentterminal/update/${this.$route.params.id}` : 'paymentterminal/create';
        delete this.terminal.id;
        delete this.terminal.status;
        delete this.terminal.createdOn;
        this.$http.post(url, this.terminal)
          .then((res) => {
            if (res.body.status === 'Success') {
              this.success = this.isEdit ? 'Terminal updated successfully' : 'Terminal created successfully';
              setTimeout(() => {
                if (this.isEdit) {
                  this.success = '';
                } else {
                  this.$router.push({name: 'paymentTerminals'});
                }
              }, 3000);
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
    },
    created() {
      this.init()
        .then(() => {
          if (this.$route.params.id) {
            this.isEdit = true;
            if (!localStorage.selectedTerminal) {
              this.$router.push({name: 'paymentTerminals'});
              return;
            }
            this.$http.get(`paymentterminal/${this.$route.params.id}?companyId=${localStorage.selectedTerminal}`)
              .then((res) => {
                if (res.body.status === 'Success') {
                  this.terminal = res.body.terminal;
                  this.hasPublicKeyBE = res.body.terminal.hasPublicKey;
                  this.createdOn = this.formatDate(this.terminal.createdOn);
                  if (this.terminal.modifiedOn) {
                    this.modifiedOn = this.formatDate(this.terminal.modifiedOn);
                  }
                  return;
                }
                this.err = res.body.statusText
              }, (err) => {
                this.err = err.body.statusText || err.body.message;
              })
          }
        })
    },
  };
</script>




// WEBPACK FOOTER //
// PaymentTerminalDetails.vue?196a0f1a